import dayjs, { ManipulateType } from 'dayjs';
import { SimplePopoverPlainOption } from '@dev/ee-components';
import { CampaignStatus, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { filter } from 'lodash-es';

import i18n from '@/../i18n';
import { CampaignsInterface } from '@/store/campaigns/types';

export enum DateDropdownOptions {
    All = 'all',
    Calendar = 'calendar',
    Last7days = 'last7days',
    Last14days = 'last14days',
    Last30days = 'last30days',
    Last3months = 'last3months',
    Last6months = 'last6months',
    Last12months = 'last12months',
}

export const defaultDateDropdownOption = DateDropdownOptions.Last30days;

type DateDropdownOptionItem = {
    value: DateDropdownOptions;
    valueInUnit: number;
    unit: string;
    label: string;
};

export type DateDropdownOptionConfig = Record<DateDropdownOptions, DateDropdownOptionItem>;

export const getDateDropdownOptionsConfig = (): DateDropdownOptionConfig => ({
    [DateDropdownOptions.All]: {
        label: i18n.t('all'),
        value: DateDropdownOptions.All,
        valueInUnit: 0,
        unit: '',
    },
    [DateDropdownOptions.Calendar]: {
        label: i18n.t('specificdaterange'),
        value: DateDropdownOptions.Calendar,
        valueInUnit: 0,
        unit: '',
    },
    [DateDropdownOptions.Last7days]: {
        label: i18n.t('last7days'),
        value: DateDropdownOptions.Last7days,
        valueInUnit: 7,
        unit: 'days',
    },
    [DateDropdownOptions.Last14days]: {
        label: i18n.t('last14days'),
        value: DateDropdownOptions.Last14days,
        valueInUnit: 14,
        unit: 'days',
    },
    [DateDropdownOptions.Last30days]: {
        label: i18n.t('last30days'),
        value: DateDropdownOptions.Last30days,
        valueInUnit: 30,
        unit: 'days',
    },
    [DateDropdownOptions.Last3months]: {
        label: i18n.t('last3months'),
        value: DateDropdownOptions.Last3months,
        valueInUnit: 3,
        unit: 'months',
    },
    [DateDropdownOptions.Last6months]: {
        label: i18n.t('last6months'),
        value: DateDropdownOptions.Last6months,
        valueInUnit: 6,
        unit: 'months',
    },
    [DateDropdownOptions.Last12months]: {
        label: i18n.t('lastyear'),
        value: DateDropdownOptions.Last12months,
        valueInUnit: 12,
        unit: 'months',
    },
});

export const getDateRangeData = (isMobile: boolean): any[] => {
    const options = getDateDropdownOptionsConfig();
    const allOption = {
        label: options.all.label,
        value: options.all.value,
    };
    const calendarOption = {
        label: options.calendar.label,
        value: options.calendar.value,
        borderBottom: true,
    };
    const dateRangesOptions = [
        {
            label: options.last7days.label,
            value: options.last7days.value,
        },
        {
            label: options.last14days.label,
            value: options.last14days.value,
        },
        {
            label: options.last30days.label,
            value: options.last30days.value,
        },
        {
            label: options.last3months.label,
            value: options.last3months.value,
        },
        {
            label: options.last6months.label,
            value: options.last6months.value,
        },
        {
            label: options.last12months.label,
            value: options.last12months.value,
        },
    ];
    if (isMobile) {
        return [allOption, ...dateRangesOptions, calendarOption];
    }
    return [calendarOption, ...dateRangesOptions, allOption];
};

interface DateRangeValues {
    from: string | null;
    to: string | null;
}

export const getDateRangeValues = (option: DateDropdownOptions): DateRangeValues => {
    if (option !== DateDropdownOptions.All) {
        const dateDropdownConfig = getDateDropdownOptionsConfig();
        const to = dayjs().endOf('day').format();
        const { valueInUnit, unit } = dateDropdownConfig[option];
        const from = dayjs()
            .subtract(valueInUnit, unit as ManipulateType)
            .startOf('day')
            .format();
        return { from, to };
    }
    return { from: null, to: null };
};

export const getDefaultDateRangeValue = (fromDate: string, toDate: string): DateDropdownOptions => {
    if (fromDate) {
        const from = dayjs(fromDate);
        const to = dayjs(toDate ?? '').endOf('day');
        const today = dayjs().endOf('day');
        if (to.isSame(today)) {
            const options = getDateDropdownOptionsConfig();
            const selectedOption: string | undefined = Object.keys(options).find((key: string): boolean => {
                const optionFrom = dayjs().subtract(options[key].valueInUnit, options[key].unit).startOf('day');
                return from.isSame(optionFrom);
            });
            return options[selectedOption]?.value ?? DateDropdownOptions.Calendar;
        } else {
            return DateDropdownOptions.Calendar;
        }
    }
    return DateDropdownOptions.All;
};

export const getCampaignTypeOptions = () => (
    [
        { label: i18n.t('regularcampaign'), value: CampaignTypeFilter.SendNow},
        { label: i18n.t('abtest'), value: CampaignTypeFilter.AbSplit},
    ]
)

export const getStatusOptions = (
    campaigns: CampaignsInterface,
    filterEmpty = true
): Pick<SimplePopoverPlainOption, 'label' | 'value' | 'count'>[] => {
    const possibleStatuses = [
        {
            label: i18n.t('completed'),
            value: CampaignStatus.Completed,
            count: campaigns.statusCount?.completed,
        },
        {
            label: i18n.t('scheduled'),
            value: CampaignStatus.Active,
            count: campaigns.statusCount?.active,
        },
        {
            label: i18n.t('cancelled'),
            value: CampaignStatus.Cancelled,
            count: campaigns.statusCount?.cancelled,
        },
        {
            label: i18n.t('sending'),
            value: CampaignStatus.Sending,
            count: campaigns.statusCount?.sending,
        },
        {
            label: i18n.t('paused'),
            value: CampaignStatus.Paused,
            count: campaigns.statusCount?.paused,
        },

        {
            label: i18n.t('processing'),
            value: CampaignStatus.Processing,
            count: campaigns.statusCount?.processing,
        },
    ];

    if (filterEmpty) {
        return filter(possibleStatuses, (status): boolean => status.count > 0);
    }
    return possibleStatuses;
};

export interface MobileFilterOption {
    value: string | number;
    label: string;
    count?: string | number;
}

export const getMobileStatusOptions = (campaigns: CampaignsInterface): MobileFilterOption[] =>
    getStatusOptions(campaigns, true) as MobileFilterOption[];

export const getMobileDateRangeOptions = (): MobileFilterOption[] => getDateRangeData(true) as MobileFilterOption[];

export const screenCampaignTypes = [CampaignTypeFilter.SendNow, CampaignTypeFilter.AbSplit];

export enum CampaignStatusNone {
    Empty = '',
}

export type CampaignStatusExtended = CampaignStatus | CampaignStatusNone;
